import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}.`);
      navigate(user.has_profile ? "/discover" : "/profile/edit", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/discover";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const error = new URLSearchParams(location.search).get("error");

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
        data-testid="login-page"
      >
        <Link to="/" className="block text-center mb-12" data-testid="logo-link">
          <span className="font-display text-3xl tracking-tight text-gold">Wanted</span>
        </Link>

        <div className="glass-strong rounded-3xl p-10">
          <div className="text-gold-soft tracking-overline text-xs mb-3 text-center">SIGN IN</div>
          <h1 className="font-display text-4xl font-light text-center mb-10 leading-tight">Welcome back.</h1>

          {error === "oauth" && (
            <div className="mb-6 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-200">
              Google sign-in failed. Please try again.
            </div>
          )}

          <button
            type="button"
            onClick={handleGoogle}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-full border border-white/15 hover:border-[#D4AF37]/50 transition-colors mb-6"
            data-testid="google-signin-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" opacity=".6"/><path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" opacity=".4"/><path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" opacity=".8"/></svg>
            <span className="text-sm">Continue with Google</span>
          </button>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs tracking-overline text-white/40">OR</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email" required placeholder="Email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="input-luxe" data-testid="login-email-input"
            />
            <input
              type="password" required placeholder="Password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="input-luxe" data-testid="login-password-input"
            />
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs text-white/50 hover:text-gold transition-colors" data-testid="forgot-link">
                Forgot password?
              </Link>
            </div>
            <button type="submit" disabled={loading} className="btn-gold w-full" data-testid="login-submit-btn">
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <p className="text-center text-sm text-white/50 mt-8">
            New to Wanted?{" "}
            <Link to="/register" className="text-gold hover:text-gold-soft transition-colors" data-testid="goto-register">
              Create an account
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
